<template>
  <div class="container vote">
    <div v-if="loading" class="loading">
      <p>Loading ballot...</p>
    </div>
    <div v-else-if="!ballot" class="loading">
      <p>No published ballot is available for this meeting.</p>
    </div>
    <div v-else>
      <div v-if="isTestMode" class="test-banner">
        TEST MODE — this vote will be flagged and excluded from real results
      </div>
      <h3 class="title is-3 has-text-centered">
        ST. MARY'S NATIVE CORPORATION BALLOT AND PROXY
      </h3>
      <h5 class="title is-5 has-text-centered">
        Solicited by and on behalf of the Board of Directors
      </h5>
      <br />
      <BallotRenderer
        :ballot="ballot"
        :shareholder="activeShareholder"
        mode="vote"
        :share-value="shareValue"
        :on-submit="onSubmit"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import BallotRenderer from '@/components/ballot/BallotRenderer.vue'
import { useUserStore } from '@/stores/user'
import { useMeetingStore } from '@/stores/meeting'
import {
  ref as storageRef,
  uploadString,
  getDownloadURL,
} from 'firebase/storage'
import {
  addDoc,
  serverTimestamp,
} from 'firebase/firestore'
import { storage, sigs, auth, votesCollection, mail } from '@/firebase'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const userStore = useUserStore()
const meetingStore = useMeetingStore()

const ballot = ref(null)
const meeting = ref(null)
const loading = ref(true)
const shareValue = ref(2)

const testShareholder = { name: 'Test Voter', shn: 9999999, shares: 100 }
const isTestMode = computed(() => route.query.test === 'true' && userStore.isAdmin)
const activeShareholder = computed(() =>
  isTestMode.value ? testShareholder : userStore.profile,
)

onMounted(async () => {
  const meetingId = route.params.meeting_id
  const pair = await meetingStore.loadMeetingById(meetingId)
  if (pair) {
    meeting.value = pair.meeting
    ballot.value = pair.ballot
    if (typeof pair.meeting.shareValue === 'number') {
      shareValue.value = pair.meeting.shareValue
    }
  }
  loading.value = false
})

async function onSubmit(payload) {
  try {
    const sh = activeShareholder.value
    const dataUrl = payload.signatureDataUrl
    let downloadURL = null
    if (dataUrl) {
      const uid = auth.currentUser.uid
      const rand = Math.random().toString(36).slice(2, 10)
      const fileName = `${Date.now()}-${rand}.png`
      const sigRef = storageRef(storage, `votes/signatures/${uid}/${fileName}`)
      await uploadString(sigRef, dataUrl, 'data_url', { contentType: 'image/png' })
      downloadURL = await getDownloadURL(sigRef)
    }

    const voteDoc = {
      createdOn: serverTimestamp(),
      user: auth.currentUser.uid,
      meeting: meeting.value.id,
      ballotVersion: ballot.value?.version || 1,
      ballotId: ballot.value?.id || null,
      sig: downloadURL,
      vote: {
        name: sh.name,
        registrationnum: sh.shn,
        totalshares: payload.totalshares,
        answers: payload.answers,
        signcustodian: payload.signcustodian,
        custodian: payload.custodian,
        custodianshn: payload.custodianshn,
        sigx: payload.sigx,
        witness1: payload.witness1,
        witness2: payload.witness2,
      },
    }
    if (isTestMode.value) voteDoc.test = true

    await addDoc(votesCollection, voteDoc)

    if (!isTestMode.value) {
      await addDoc(mail, {
        to: meetingStore.notificationEmails,
        message: {
          subject: `A new vote has been cast for ${sh.name}`,
          text: `${sh.name} has submitted an online vote.`,
          html: buildNotificationHtml(voteDoc, ballot.value),
        },
      })
    }

    toast.add({ severity: 'success', summary: 'Vote submitted', life: 2500 })
    router.push(isTestMode.value ? '/admin/votes' : '/dashboard')
  } catch (err) {
    console.error('submit error', err)
    toast.add({
      severity: 'error',
      summary: 'Submit failed',
      detail: err.message || String(err),
      life: 4000,
    })
  }
}

function buildNotificationHtml(voteDoc, ballotDoc) {
  const v = voteDoc.vote
  const qMap = {}
  for (const c of ballotDoc?.components || []) {
    if (c.type === 'ballotQuestion') qMap[c.id] = c.props
  }
  let answersHtml = ''
  for (const [qid, ans] of Object.entries(v.answers || {})) {
    const q = qMap[qid]
    answersHtml += `<p><strong>${q?.title || qid}</strong>: ${ans.choice?.toUpperCase() || ''}</p>`
    if (ans.choice === 'b') {
      for (const [cid, n] of Object.entries(ans.directed || {})) {
        const name = (q?.candidates || []).find((c) => c.id === cid)?.name || cid
        answersHtml += `<p style="padding-left:16px;">${name}: <strong>${n}</strong></p>`
      }
    }
  }
  return `
    <p>${v.name} has submitted an online vote.</p>
    <p>Registration Number: <strong>${v.registrationnum}</strong></p>
    <p>Total shares: <strong>${v.totalshares}</strong></p>
    ${answersHtml}
    <p>Signing for a custodian: <strong>${v.signcustodian}</strong></p>
    ${v.signcustodian ? `<p>Custodian Name: <strong>${v.custodian}</strong></p>` : ''}
    ${v.signcustodian ? `<p>Custodian Registration Number: <strong>${v.custodianshn}</strong></p>` : ''}
    <p>Signing with an X: <strong>${v.sigx}</strong></p>
    ${v.sigx ? `<p>Witness 1: <strong>${v.witness1}</strong></p>` : ''}
    ${v.sigx ? `<p>Witness 2: <strong>${v.witness2}</strong></p>` : ''}
  `
}
</script>

<style lang="scss" scoped>
.vote {
  margin-top: 50px;
  padding: 20px;
}
.loading {
  padding: 60px;
  text-align: center;
  color: #777;
}
.test-banner {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 10px 16px;
  text-align: center;
  font-weight: 600;
  margin-bottom: 16px;
}
</style>
